import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { QueryState } from "@/shared/ui/QueryState";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, ClipboardListIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { formatPurchaseOrderApiError } from "../api/purchase-orders-client";
import {
  usePurchaseOrderQuery,
  useUpdatePurchaseOrderDraft,
} from "../api/purchase-orders-queries";
import { useSuppliersLookupQuery } from "../api/suppliers-queries";
import { PoFormDateField } from "../ui/purchase/PoFormDateField";
import { PurchaseOrderLineEditor } from "../ui/purchase/PurchaseOrderLineEditor";
import {
  newDraftLine,
  toLinePayload,
  type PoDraftLineRow,
} from "../ui/purchase/poDraftUtils";
import { PO_MENU_TITLE } from "../ui/purchase/purchaseLaoLabels";

export function PurchaseOrderEditPage() {
  const { id } = useParams({
    from: "/app/inventory/purchase-orders/$id/edit",
  });
  const nav = useNavigate({ from: "/app/inventory/purchase-orders/$id/edit" });
  const query = usePurchaseOrderQuery(id);
  const updateDraft = useUpdatePurchaseOrderDraft(id);
  const suppliers = useSuppliersLookupQuery();

  const [supplierId, setSupplierId] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<PoDraftLineRow[]>([]);

  const po = query.data;

  useEffect(() => {
    if (!po) return;
    if (po.status !== "DRAFT") {
      nav({
        to: "/app/inventory/purchase-orders/$id",
        params: { id },
        replace: true,
      });
      return;
    }
    setSupplierId(po.supplierId);
    setOrderDate(po.orderDate);
    setExpectedDate(po.expectedDate ?? "");
    setNote(po.note ?? "");
    setLines(
      po.lines.map((l) => ({
        key: l.id,
        itemType: l.itemType,
        itemId: l.itemId,
        qtyOrdered: l.qtyOrdered,
        unitCostLak: l.unitCostLak,
        note: l.note,
      })),
    );
  }, [po, id, nav]);

  const supplierOptions = useMemo(
    () =>
      (suppliers.data ?? []).map((s) => ({
        value: s.id,
        label: s.code ? `${s.name} (${s.code})` : s.name,
      })),
    [suppliers.data],
  );

  const validate = (): string | null => {
    if (!supplierId) return "ຕ້ອງເລືອກຜູ້ສະໜອງ";
    if (!orderDate) return "ຕ້ອງໃສ່ວັນທີສັ່ງ";
    if (lines.length === 0) return "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ບັນທັດ";
    for (const line of lines) {
      if (!line.itemId) return "ທຸກບັນທັດຕ້ອງເລືອກລາຍການ";
      if (line.qtyOrdered <= 0) return "ປະລິມານຕ້ອງໃຫຍ່ກວ່າ 0";
    }
    return null;
  };

  const onSave = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      await updateDraft.mutateAsync({
        supplierId,
        orderDate,
        expectedDate: expectedDate.trim() ? expectedDate : null,
        note: note.trim() ? note : null,
        lines: toLinePayload(lines),
      });
      toast.success("ບັນທຶກ PO ສຳເລັດ");
      nav({
        to: "/app/inventory/purchase-orders/$id",
        params: { id },
      });
    } catch (e) {
      toast.error(formatPurchaseOrderApiError(e));
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: PO_MENU_TITLE, to: "/app/inventory/purchase-orders" },
            { label: po?.poNo ?? "…" },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>
      <Main>
        <QueryState result={query} title="ກຳລັງໂຫຼດ" variant="fullscreen">
          {!po || po.status !== "DRAFT" ? null : (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="font-bold text-2xl tracking-tight">
                    ແກ້ໄຂ {po.poNo}
                  </h2>
                  <p className="text-muted-foreground">ຮ່າງ (DRAFT)</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() =>
                    nav({
                      to: "/app/inventory/purchase-orders/$id",
                      params: { id },
                    })
                  }
                >
                  <ArrowLeftIcon className="size-4" />
                  ກັບຄືນ
                </Button>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <ClipboardListIcon className="size-5" />
                      ຫົວ PO
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <span className="font-medium text-sm">ຜູ້ສະໜອງ *</span>
                      <SimpleSelect
                        placeholder="ເລືອກຜູ້ສະໜອງ"
                        value={supplierId}
                        onValueChange={setSupplierId}
                        options={supplierOptions}
                      />
                    </div>
                    <PoFormDateField
                      label="ວັນທີສັ່ງ"
                      value={orderDate}
                      onChange={setOrderDate}
                      requiredMark
                    />
                    <PoFormDateField
                      label="ຄາດວ່າຈະຮັບ"
                      value={expectedDate}
                      onChange={setExpectedDate}
                      placeholder="ທາງເລືອກ"
                    />
                    <div className="space-y-2 sm:col-span-2">
                      <span className="font-medium text-sm">ໝາຍເຫດ</span>
                      <Input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">ບັນທັດສັ່ງຊື້</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PurchaseOrderLineEditor lines={lines} onChange={setLines} />
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6 flex justify-end gap-2 border-t pt-6">
                <Button onClick={onSave} isLoading={updateDraft.isPending}>
                  ບັນທຶກ
                </Button>
              </div>
            </>
          )}
        </QueryState>
      </Main>
    </>
  );
}
